import { useMemo, useRef, useState } from "react";
import {
  GraphNode,
  GraphEdge
} from "modules/ontology/data/ontology-graph.model";
import {
  OntologyService,
  OntologyNodeGraphViewResponse,
  OntologyNodeTraceabilityBusinessQuestion,
  OntologyNodeTraceabilityCompetencyQuestion
} from "../../../../data/ontology.service";
import {
  ConceptRow,
  AttributeRow
} from "../../../../data/create-ontology-entities-relationships.data";

export interface RelatedEntitiesConnectedNode {
  id: string;
  nodeId: string;
  nodeName: string;
  nodeType: string;
  relationLabel: string;
}

export interface RelatedEntitiesSelectedNodeDetail {
  conceptRow: ConceptRow;
  businessQuestions: OntologyNodeTraceabilityBusinessQuestion[];
  competencyQuestions: OntologyNodeTraceabilityCompetencyQuestion[];
}

// Owns the "second level" (double-click -> ego subgraph) feature end to end: fetching,
// search, and the derived focused/connected-node view models used by both the overlay
// canvas and the Graph Directory panel. Kept separate from the main canvas's own state so
// drilling into another node *inside* this subgraph never touches/reloads the main canvas.
export const useRelatedEntitiesGraph = (initiativeId?: string) => {
  const [nodeId, setNodeId] = useState<string | null>(null);
  const [data, setData] = useState<OntologyNodeGraphViewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  // Guards against overlapping fetches (double-click spam) without needing extra state.
  const isFetchingRef = useRef(false);
  // Single-clicked node inside the ego subgraph — shows its info + traceability in the
  // Graph Directory panel without changing which node the subgraph is focused around.
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedNodeDetail, setSelectedNodeDetail] =
    useState<RelatedEntitiesSelectedNodeDetail | null>(null);
  const [isLoadingSelectedNodeDetail, setIsLoadingSelectedNodeDetail] =
    useState(false);
  // The selected node's own attributes, fetched directly (not read off the subgraph's
  // embedded attribute list, which can be a lighter/incomplete shape).
  const [selectedNodeAttributes, setSelectedNodeAttributes] = useState<
    AttributeRow[]
  >([]);

  // Opens the subgraph for `id`, used both for the initial double-click on the main canvas
  // and for drilling into a different node from inside the overlay's own canvas. The
  // previous `data` is intentionally kept until the new response arrives so the overlay
  // never has to unmount/re-blur the main canvas while drilling deeper.
  const open = (id: string) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setNodeId(id);
    setSearch("");
    setSelectedNodeId(null);
    setSelectedNodeDetail(null);
    setIsLoading(true);
    OntologyService.getNodeGraphView(id)
      .then(setData)
      .finally(() => {
        setIsLoading(false);
        isFetchingRef.current = false;
      });
  };

  const close = () => {
    setNodeId(null);
    setData(null);
    setSearch("");
    setSelectedNodeId(null);
    setSelectedNodeDetail(null);
  };

  // Single-clicking a node in the overlay's canvas (or the connected-nodes list) shows its
  // full detail + Ontology Traceability in the Graph Directory panel.
  const selectNode = (id: string) => {
    setSelectedNodeId(id);
    setSelectedNodeDetail(null);
    setSelectedNodeAttributes([]);
    if (!initiativeId) return;
    setIsLoadingSelectedNodeDetail(true);
    OntologyService.getNodeDetail(initiativeId, id)
      .then(setSelectedNodeDetail)
      .finally(() => setIsLoadingSelectedNodeDetail(false));
    OntologyService.getNodeAttributes(initiativeId, id).then(
      setSelectedNodeAttributes
    );
  };

  const clearSelectedNode = () => {
    setSelectedNodeId(null);
    setSelectedNodeDetail(null);
    setSelectedNodeAttributes([]);
  };

  const focusedNode = useMemo(() => {
    if (!data) return null;
    return (
      data.nodes.find((node) => node.nodeId === nodeId || node.id === nodeId) ??
      null
    );
  }, [data, nodeId]);

  const connectedNodes = useMemo((): RelatedEntitiesConnectedNode[] => {
    if (!data || !focusedNode) return [];
    const focusedId = focusedNode.id;
    const focusedName = focusedNode.label;
    const nodeById = new Map(data.nodes.map((node) => [node.id, node]));
    return data.edges
      .filter((edge) => edge.source === focusedId || edge.target === focusedId)
      .map((edge) => {
        const connectedIsSource = edge.target === focusedId;
        const connectedId = connectedIsSource ? edge.source : edge.target;
        const connectedNode = nodeById.get(connectedId);
        const connectedName = connectedNode?.label ?? connectedId;
        const connectedType = connectedNode?.nodeType ?? "";
        const relationLabel = connectedIsSource
          ? `${edge.relationshipName} → ${focusedName}`
          : `${focusedName} ${edge.relationshipName} ${connectedName}`;
        return {
          id: `${edge.id}-${connectedId}`,
          nodeId: connectedId,
          nodeName: connectedName,
          nodeType: connectedType,
          relationLabel
        };
      });
  }, [data, focusedNode]);

  const filteredConnectedNodes = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return connectedNodes;
    return connectedNodes.filter((entry) =>
      entry.nodeName.toLowerCase().includes(query)
    );
  }, [connectedNodes, search]);

  // Attributes for the single-clicked node, fetched directly in `selectNode` (above) rather
  // than read off the subgraph response, which only carries a lighter attribute shape.

  const subGraph = useMemo(() => {
    if (!data) return null;
    const nodes: GraphNode[] = data.nodes.map((node) => ({
      id: node.id,
      label: node.label,
      type: node.nodeType,
      x: 0,
      y: 0
    }));
    const edges: GraphEdge[] = data.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      relationship: edge.relationshipName
    }));
    return { nodes, edges };
  }, [data]);

  return {
    // Open whenever a nodeId is set, regardless of whether its data has loaded yet.
    isOpen: nodeId !== null,
    // True only for the very first fetch (no subgraph yet) — the signal the main canvas
    // uses to blur itself; drilling into another node afterwards keeps `subGraph` set.
    isInitialLoading: isLoading && !subGraph,
    isLoading,
    nodeId,
    data,
    search,
    setSearch,
    focusedNode,
    connectedNodes,
    filteredConnectedNodes,
    subGraph,
    open,
    close,
    selectedNodeId,
    selectedNodeDetail,
    selectedNodeAttributes,
    isLoadingSelectedNodeDetail,
    selectNode,
    clearSelectedNode
  };
};
